/** @jsxImportSource @emotion/react */
import {
  type ApiScan,
  getAudienceDocResidentResponsibleOnly,
} from '@kaiizen/kai-pro-api';
import {
  useAudienceDocShareLinkMutators,
  useAudienceDocShareLinkStatus,
  useTaskModifierDisplayNames,
} from '@kaiizen/kai-pro-api-react-state';

import { useToast } from '@/Client/State';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';
import { useAudienceDocShareLinkReplacementConfirmation } from '../../useAudienceDocShareLinkReplacementConfirmation';
import { ArtifactSelectableCard } from './ArtifactSelectableCard';
import { formatArtifactDate } from './FocusedArtifactPanelLib';
import {
  BADGE_ROW_STYLE,
  badgeStyle,
  CARD_DIVIDER_STYLE,
  CARD_KICKER_STYLE,
  CARD_SUBTITLE_STYLE,
  CARD_TITLE_STYLE,
  COPY_CARD_ACTIONS_STYLE,
  COPY_CARD_STYLE,
  PRIMARY_ACTION_BUTTON_STYLE,
  SECONDARY_ACTION_BUTTON_STYLE,
} from './FocusedArtifactPanelStyleLib';

/** Share-ready audience-doc card with link management actions. */
export const AudienceDocArtifactCard = ({
  audienceDoc,
  canManageShare,
  featureEnabled,
  isSelected,
  parentArtifactTitle,
  onSelect,
  onOpen,
}: {
  audienceDoc: ApiScan;
  canManageShare: boolean;
  featureEnabled: boolean;
  isSelected: boolean;
  parentArtifactTitle: string;
  onSelect: () => void;
  onOpen: () => void;
}) => {
  const { showToast, showErrorToast } = useToast();
  const modifierLabels = useTaskModifierDisplayNames();
  const confirmAudienceDocShareLinkReplacement = useAudienceDocShareLinkReplacementConfirmation();
  const { shareLinkStatus, refetch } = useAudienceDocShareLinkStatus(
    audienceDoc.id,
    { enabled: canManageShare },
  );
  const {
    createAudienceDocShareLink,
    isCreatingAudienceDocShareLink,
  } = useAudienceDocShareLinkMutators();
  const hasActiveLink = !!shareLinkStatus?.hasActiveLink;

  const copyLink = async () => {
    try {
      const { url } = await createAudienceDocShareLink(audienceDoc.id);
      await navigator.clipboard.writeText(url);
      await refetch();
      showToast(
        hasActiveLink
          ? 'Copied a new audience doc link. The previous link is now inactive.'
          : 'Audience doc link copied to clipboard.',
        'success',
      );
    } catch (error) {
      showErrorToast(error, 'copy an audience doc link');
    }
  };

  const requestCopyLink = () =>
    hasActiveLink
      ? confirmAudienceDocShareLinkReplacement(copyLink)
      : copyLink();

  return (
    <ArtifactSelectableCard
      selected={isSelected}
      extraCss={COPY_CARD_STYLE}
      title={`View ${audienceDoc.name || 'Audience Doc'}`}
      onSelect={onSelect}
    >
      <div css={CARD_KICKER_STYLE}>Audience doc</div>
      <div css={CARD_TITLE_STYLE}>{audienceDoc.name || 'Audience Doc'}</div>
      <div css={CARD_SUBTITLE_STYLE}>
        Based on {parentArtifactTitle} · created {formatArtifactDate(audienceDoc.createdAt)}
      </div>
      <div css={BADGE_ROW_STYLE}>
        <div css={badgeStyle('accent')}>Copy</div>
        {getAudienceDocResidentResponsibleOnly(audienceDoc) && <div css={badgeStyle('neutral')}>{modifierLabels.tenantResponsibility} only</div>}
        {hasActiveLink
          ? <div css={badgeStyle('accent')}>Expires {formatArtifactDate(shareLinkStatus?.expiresAt)}</div>
          : <div css={badgeStyle('neutral')}>No active link</div>}
      </div>
      <div css={CARD_DIVIDER_STYLE} />
      <div css={CARD_SUBTITLE_STYLE}>
        {hasActiveLink
          ? 'Public link is active for this copy.'
          : featureEnabled
          ? 'Create a public link when you are ready to share this copy.'
          : 'Public link creation is disabled for this organization right now.'}
      </div>
      <div css={COPY_CARD_ACTIONS_STYLE}>
        {featureEnabled && canManageShare && (
          <CommonActionButton
            style={SECONDARY_ACTION_BUTTON_STYLE}
            onClick={async event => {
              event.stopPropagation();
              await requestCopyLink();
            }}
            disabled={isCreatingAudienceDocShareLink}
          >
            {isCreatingAudienceDocShareLink ? 'Copying...' : hasActiveLink ? 'Copy New Link' : 'Copy Link'}
          </CommonActionButton>
        )}
        <CommonActionButton
          style={PRIMARY_ACTION_BUTTON_STYLE}
          onClick={event => {
            event.stopPropagation();
            onOpen();
          }}
        >
          Open
        </CommonActionButton>
      </div>
    </ArtifactSelectableCard>
  );
};
